import type { API } from 'geoview-core/api/api';
import type { MapViewer } from 'geoview-core/geo/map/map-viewer';
import type { ControllerRegistry } from 'geoview-core/core/controllers/base/controller-registry';
import { GVAbstractTestSuite } from './abstract-gv-test-suite';
import { UtilitiesCoreTester } from '../testers/utilities-core-tester';
import { UtilitiesDateTester } from '../testers/utilities-date-tester';
import { UtilitiesGeoTester } from '../testers/utilities-geo-tester';
import { UtilitiesProjectionTester } from '../testers/utilities-projection-tester';

/**
 * Test Suite for cgpv.api.utilities functions (core, date, geo, projection).
 */
export class GVTestSuiteUtilities extends GVAbstractTestSuite {
  /** The Utilities Core Tester. */
  #coreTester: UtilitiesCoreTester;

  /** The Utilities Date Tester. */
  #dateTester: UtilitiesDateTester;

  /** The Utilities Geo Tester. */
  #geoTester: UtilitiesGeoTester;

  /** The Utilities Projection Tester. */
  #projectionTester: UtilitiesProjectionTester;

  /**
   * Constructs the Test Suite.
   *
   * @param api - The shared api
   * @param mapViewer - The map viewer
   * @param controllerRegistry - The controller registry
   */
  constructor(api: API, mapViewer: MapViewer, controllerRegistry: ControllerRegistry) {
    super(api, mapViewer, controllerRegistry);
    this.#coreTester = new UtilitiesCoreTester(api, mapViewer, controllerRegistry);
    this.#dateTester = new UtilitiesDateTester(api, mapViewer, controllerRegistry);
    this.#geoTester = new UtilitiesGeoTester(api, mapViewer, controllerRegistry);
    this.#projectionTester = new UtilitiesProjectionTester(api, mapViewer, controllerRegistry);
    this.addTester(this.#coreTester);
    this.addTester(this.#dateTester);
    this.addTester(this.#geoTester);
    this.addTester(this.#projectionTester);
  }

  /**
   * Returns the name of the Test Suite.
   *
   * @returns The name of the Test Suite
   */
  override getName(): string {
    return 'Utilities Test Suite';
  }

  /**
   * Returns the description of the Test Suite.
   *
   * @returns The description of the Test Suite
   */
  override getDescriptionAsHtml(): string {
    return `Tests cgpv.api.utilities — pure function unit tests:<br/>
      <b>Core</b> — range, camelCase, deepEqual, deepClone, deepMerge, shallowEquals, isNumeric,<br/>
      &nbsp;&nbsp;generateId/isValidUUID, setAlphaColor, isJsonString, escapeRegExp, stringify,<br/>
      &nbsp;&nbsp;sanitizeHtmlContent, enhanceLinksAccessibility, getLocalizedMessage<br/>
      <b>Date</b> — formatDate, ISO short, toMilliseconds, tryParseDate, hasTimeComponents,<br/>
      &nbsp;&nbsp;isValidTimezone, createRangeOGC, epoch timestamps, US standard, special formats<br/>
      <b>Geo</b> — getBaseUrl, getMapServerUrl, DMS format, isPointInExtent, extent union/intersection,<br/>
      &nbsp;&nbsp;bufferExtent, isGeoJSONObject, geometryType conversions, ensureServiceRequestUrl, GeoJSON/EsriJSON projection readers<br/>
      <b>Projection</b> — projection names, readEPSGNumber, getLonLat, transformPoints, transformExtent, object/identifier/metadata dispatch`;
  }

  /**
   * Gets the number of active tests launched by the full suite.
   *
   * @returns The number of active full-suite tester calls, excluding debug-only calls
   */
  override getTestsTotalFinal(): number {
    return 57;
  }

  /**
   * Overrides the implementation to perform the tests for this Test Suite.
   *
   * @returns A promise that resolves when tests are completed
   */
  protected override async onLaunchTestSuite(): Promise<unknown> {
    // Core tester tests
    const pRange = this.#coreTester.testRange();
    const pCamelCase = this.#coreTester.testCamelCase();
    const pDeepEqual = this.#coreTester.testDeepEqual();
    const pDeepClone = this.#coreTester.testDeepClone();
    const pDeepMerge = this.#coreTester.testDeepMerge();
    const pShallowEquals = this.#coreTester.testShallowEquals();
    const pIsNumeric = this.#coreTester.testIsNumeric();
    const pIsObjectEmpty = this.#coreTester.testIsObjectEmpty();
    const pGenerateIdAndIsValidUUID = this.#coreTester.testGenerateIdAndIsValidUUID();
    const pSetAlphaColor = this.#coreTester.testSetAlphaColor();
    const pIsJsonString = this.#coreTester.testIsJsonString();
    const pJsonParsing = this.#coreTester.testJsonParsing();
    const pEscapeRegExp = this.#coreTester.testEscapeRegExp();
    const pIsImage = this.#coreTester.testIsImage();
    const pStringify = this.#coreTester.testStringify();
    const pSafeStringify = this.#coreTester.testSafeStringify();
    const pDeepMergeObjects = this.#coreTester.testDeepMergeObjects();
    const pFindPropertyByRegexPath = this.#coreTester.testFindPropertyByRegexPath();
    const pFormatMeasurements = this.#coreTester.testFormatMeasurements();
    const pNormalizeDatacubeAccessPath = this.#coreTester.testNormalizeDatacubeAccessPath();
    const pSanitizeHtmlContent = this.#coreTester.testSanitizeHtmlContent();
    const pEnhanceLinksAccessibility = this.#coreTester.testEnhanceLinksAccessibility();
    const pGetLocalizedMessage = this.#coreTester.testGetLocalizedMessage();
    const pTransformMarkdownIds = this.#coreTester.testTransformMarkdownIds();

    // Date tester tests
    const pFormatDate = this.#dateTester.testFormatDate();
    const pFormatDateISOShort = this.#dateTester.testFormatDateISOShort();
    const pConvertToMilliseconds = this.#dateTester.testConvertToMilliseconds();
    const pTryParseDate = this.#dateTester.testTryParseDate();
    const pHasTimeComponents = this.#dateTester.testHasTimeComponents();
    const pIsValidTimezone = this.#dateTester.testIsValidTimezone();
    const pCreateRangeOGC = this.#dateTester.testCreateRangeOGC();
    const pDateConstants = this.#dateTester.testDateConstants();
    const pParseDateToDayjs = this.#dateTester.testParseDateToDayjs();
    const pDatesEpochTimestamps = this.#dateTester.testDatesEpochTimestamps();
    const pDatesUSStandard = this.#dateTester.testDatesUSStandard();
    const pDatesSpecialFormats = this.#dateTester.testDatesSpecialFormats();

    // Geo tester tests
    const pGetBaseUrl = this.#geoTester.testGetBaseUrl();
    const pGetMapServerUrl = this.#geoTester.testGetMapServerUrl();
    const pCoordFormatDMS = this.#geoTester.testCoordFormatDMS();
    const pIsPointInExtent = this.#geoTester.testIsPointInExtent();
    const pGetExtentUnion = this.#geoTester.testGetExtentUnion();
    const pIsExtentLonLat = this.#geoTester.testIsExtentLonLat();
    const pBufferExtent = this.#geoTester.testBufferExtent();
    const pIsGeoJSONObject = this.#geoTester.testIsGeoJSONObject();
    const pGeometryTypeConversions = this.#geoTester.testGeometryTypeConversions();
    const pEnsureServiceRequestUrl = this.#geoTester.testEnsureServiceRequestUrl();
    const pGetExtentIntersection = this.#geoTester.testGetExtentIntersection();

    // Projection tester tests
    const pProjectionNames = this.#projectionTester.testProjectionNames();
    const pReadEPSGNumber = this.#projectionTester.testReadEPSGNumber();
    const pGetProjectionLonLat = this.#projectionTester.testGetProjectionLonLat();
    const pTransformPoints = this.#projectionTester.testTransformPoints();
    const pTransformExtentFromProj = this.#projectionTester.testTransformExtentFromProj();
    const pGetProjectionFromString = this.#projectionTester.testGetProjectionFromString();

    await Promise.all([
      this.#geoTester.testReadFeaturesFromGeoJSONProjectionObject(),
      this.#geoTester.testReadFeaturesFromGeoJSONEmbeddedCRSPrecedence(),
      this.#geoTester.testReadFeaturesFromEsriJSONProjectionObject(),
      // Core
      pRange,
      pCamelCase,
      pDeepEqual,
      pDeepClone,
      pDeepMerge,
      pShallowEquals,
      pIsNumeric,
      pIsObjectEmpty,
      pGenerateIdAndIsValidUUID,
      pSetAlphaColor,
      pIsJsonString,
      pJsonParsing,
      pEscapeRegExp,
      pIsImage,
      pStringify,
      pSafeStringify,
      pDeepMergeObjects,
      pFindPropertyByRegexPath,
      pFormatMeasurements,
      pNormalizeDatacubeAccessPath,
      pSanitizeHtmlContent,
      pEnhanceLinksAccessibility,
      pGetLocalizedMessage,
      pTransformMarkdownIds,
      // Date
      pFormatDate,
      pFormatDateISOShort,
      pConvertToMilliseconds,
      pTryParseDate,
      pHasTimeComponents,
      pIsValidTimezone,
      pCreateRangeOGC,
      pDateConstants,
      pParseDateToDayjs,
      pDatesEpochTimestamps,
      pDatesUSStandard,
      pDatesSpecialFormats,
      // Geo
      pGetBaseUrl,
      pGetMapServerUrl,
      pCoordFormatDMS,
      pIsPointInExtent,
      pGetExtentUnion,
      pIsExtentLonLat,
      pBufferExtent,
      pIsGeoJSONObject,
      pGeometryTypeConversions,
      pEnsureServiceRequestUrl,
      pGetExtentIntersection,
      // Projection
      pProjectionNames,
      pReadEPSGNumber,
      pGetProjectionLonLat,
      pTransformPoints,
      pTransformExtentFromProj,
      pGetProjectionFromString,
    ]);

    // Definition-request observation must not overlap the other utility tests.
    await this.#projectionTester.testAddProjectionIfMissingInputs();
    return Promise.resolve();
  }
}
